import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Title,
  Text,
  Group,
  Card,
  Stack,
  Loader,
  Center,
  Avatar,
  Button,
  Menu,
  Alert,
  SimpleGrid,
  Image,
  AspectRatio,
  Modal,
  Box,
  useMantineColorScheme
} from '@mantine/core';
import { IconVideo, IconFilter, IconInfoCircle, IconPlayerPlay, IconLock, IconChevronDown, IconCheck } from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { VideoEstudio } from '../Models/VideoEstudio';
import { Especialidad } from '../services/especialidadService';
import { videosQuery, especialidadesQuery } from '../queries/appQueries';
import QuejaSugerenciaButton from '../components/QuejaSugerenciaButton';
import enarmLogo from '../assets/enarm_logo_noletter.png';
import { colorEspecialidad, capitalizarEspecialidad as capitalizar } from '../utils/especialidad';
import PageHeader from '../components/ui/PageHeader';
import SearchInput from '../components/ui/SearchInput';
import PageContainer from '../components/ui/PageContainer';
import PageSkeleton from '../components/ui/PageSkeleton';

const VideosEstudioPage: React.FC = () => {
  const { colorScheme } = useMantineColorScheme();

  const videosQ = useQuery(videosQuery());
  const especialidadesQ = useQuery(especialidadesQuery());
  const videos: VideoEstudio[] = videosQ.data ?? [];
  const especialidades: Especialidad[] = especialidadesQ.data ?? [];
  const loading = videosQ.isLoading;
  const videosError = videosQ.error;

  const [searchTerm, setSearchTerm] = useState('');
  const [filterEspecialidad, setFilterEspecialidad] = useState<string | null>(null);

  // Carga incremental: empieza con 12 y va sumando al hacer scroll
  const PAGE = 12;
  const [visibleCount, setVisibleCount] = useState(PAGE);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Modal para ver video
  const [opened, { open, close }] = useDisclosure(false);
  const [selectedVideo, setSelectedVideo] = useState<VideoEstudio | null>(null);

  const [searchParams] = useSearchParams();

  // 403 con requiresSubscription → pantalla premium; otro error → mensaje genérico.
  const subErr = videosError as any;
  const requiresSubscription = subErr?.response?.status === 403 && subErr?.response?.data?.requiresSubscription;
  const error = videosError && !requiresSubscription
    ? 'Error al cargar los videos. Por favor, intenta de nuevo.'
    : null;

  // Si llega ?video={id} (p. ej. desde el carrusel del dashboard), abrir ese video
  useEffect(() => {
    const videoId = searchParams.get('video');
    if (!videoId || videos.length === 0) return;
    const v = videos.find((vid) => String(vid.id) === videoId);
    if (v) {
      setSelectedVideo(v);
      open();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videos, searchParams]);

  const filteredVideos = videos.filter(video => {
    const matchesSearch = video.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (video.descripcion || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesEspecialidad = !filterEspecialidad || video.especialidadId.toString() === filterEspecialidad;
    return matchesSearch && matchesEspecialidad;
  });

  // Reiniciar la cantidad visible al cambiar búsqueda/filtro
  useEffect(() => {
    setVisibleCount(PAGE);
  }, [searchTerm, filterEspecialidad]);

  // Cargar más al acercarse al final (scroll infinito)
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount((c) => c + PAGE);
        }
      },
      { rootMargin: '300px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [filteredVideos.length, visibleCount]);

  const visibleVideos = filteredVideos.slice(0, visibleCount);
  const hayMas = visibleCount < filteredVideos.length;

  const handleOpenVideo = (video: VideoEstudio) => {
    setSelectedVideo(video);
    open();
  };

  if (loading) {
    return <PageSkeleton variant="grid" />;
  }

  if (requiresSubscription) {
    return (
      <Container size="md" py="xl">
        <Card radius="xl" p="xl" withBorder style={{ textAlign: 'center' }}>
          <Stack align="center" gap="lg">
            <IconLock size={64} color="#94a3b8" />
            <Title order={2}>Contenido Premium</Title>
            <Text c="dimmed" size="lg">
              Los videos de estudio están disponibles para usuarios con suscripción activa.
            </Text>
            <Text c="dimmed" size="sm">
              Mejora tu plan para acceder a todo el contenido de video.
            </Text>
          </Stack>
        </Card>
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="xl" py="xl">
        <Alert icon={<IconInfoCircle size="1rem" />} title="Error" color="red">
          {error}
        </Alert>
      </Container>
    );
  }

  const cardStyle = {
    backgroundColor: colorScheme === 'dark' ? 'rgba(30, 41, 59, 0.7)' : 'rgba(255, 255, 255, 0.25)',
    backdropFilter: 'blur(40px) saturate(200%)',
    border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.5)'}`,
  };

  return (
    <PageContainer>
      <style>{`
        .video-card {
          border-radius: 14px;
          padding: 6px;
          cursor: pointer;
          transition: background-color 0.15s ease;
        }
        .video-card:hover {
          background-color: ${colorScheme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
        }
        .video-thumb {
          position: relative;
          width: 100%;
          height: 100%;
          border-radius: 10px;
          overflow: hidden;
          background-color: #000;
        }
        .video-thumb img {
          width: 100% !important;
          height: 100% !important;
          object-fit: cover !important;
          display: block;
        }
        .video-play {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0,0,0,0.3);
          opacity: 0;
          transition: opacity 0.15s ease;
        }
        .video-card:hover .video-play { opacity: 1; }
      `}</style>
      <Stack gap="xl">
        {/* Header */}
        <PageHeader
          title="Videos de Estudio"
          subtitle="Material en video organizado por especialidad para reforzar tu preparación"
        />

        {/* Búsqueda + filtro (sin card) */}
        <Group gap="sm" align="center" wrap="nowrap">
          <SearchInput
            placeholder="Buscar videos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ flex: 1 }}
          />
          <Menu shadow="md" width={250} position="bottom-end" withinPortal>
            <Menu.Target>
              <Button
                variant={filterEspecialidad ? 'light' : 'default'}
                radius="md"
                leftSection={<IconFilter size={16} />}
                rightSection={<IconChevronDown size={14} />}
                style={filterEspecialidad ? { color: colorEspecialidad(Number(filterEspecialidad)) } : undefined}
              >
                {filterEspecialidad
                  ? capitalizar(especialidades.find((e) => e.id.toString() === filterEspecialidad)?.nombre)
                  : 'Especialidad'}
              </Button>
            </Menu.Target>
            <Menu.Dropdown style={{ maxHeight: 320, overflowY: 'auto' }}>
              <Menu.Item
                leftSection={
                  filterEspecialidad ? <Box style={{ width: 14 }} /> : <IconCheck size={14} />
                }
                onClick={() => setFilterEspecialidad(null)}
              >
                Todas
              </Menu.Item>
              <Menu.Divider />
              {especialidades.map((esp) => {
                const seleccionada = filterEspecialidad === esp.id.toString();
                return (
                  <Menu.Item
                    key={esp.id}
                    onClick={() => setFilterEspecialidad(esp.id.toString())}
                    leftSection={
                      <Box
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: colorEspecialidad(esp.id),
                        }}
                      />
                    }
                    rightSection={seleccionada ? <IconCheck size={14} /> : null}
                  >
                    <Text size="sm" fw={seleccionada ? 700 : 500} style={{ color: colorEspecialidad(esp.id) }}>
                      {capitalizar(esp.nombre)}
                    </Text>
                  </Menu.Item>
                );
              })}
            </Menu.Dropdown>
          </Menu>
        </Group>

        {/* Grid de videos */}
        {filteredVideos.length === 0 ? (
          <Card radius="xl" p="xl" style={{ ...cardStyle, textAlign: 'center' }}>
            <Stack align="center" gap="md">
              <IconVideo size={48} color="#94a3b8" />
              <Text c="dimmed" size="lg">No se encontraron videos</Text>
              {searchTerm && (
                <Text c="dimmed" size="sm">Intenta con otros términos de búsqueda</Text>
              )}
            </Stack>
          </Card>
        ) : (
          <SimpleGrid cols={{ base: 1, xs: 2, md: 3 }} spacing="xs" verticalSpacing="md">
            {visibleVideos.map((video) => {
              const espColor = colorEspecialidad(video.especialidadId);
              return (
                <Box
                  key={video.id}
                  className="video-card"
                  onClick={() => handleOpenVideo(video)}
                >
                  {/* Thumbnail */}
                  <AspectRatio ratio={16 / 9}>
                    <Box className="video-thumb">
                      <Image
                        src={video.thumbnailUrl || `https://img.youtube.com/vi/${video.youtubeVideoId}/hqdefault.jpg`}
                        alt={video.titulo}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      {/* Play overlay (aparece al hacer hover en toda la card) */}
                      <Box className="video-play">
                        <IconPlayerPlay size={44} color="white" />
                      </Box>
                    </Box>
                  </AspectRatio>

                  {/* Info estilo YouTube: avatar + título y debajo especialidad · subespecialidad */}
                  <Group gap="xs" align="flex-start" wrap="nowrap" mt="xs">
                    <Avatar
                      src={enarmLogo}
                      size={32}
                      radius="xl"
                      style={{ flexShrink: 0, backgroundColor: '#ffffff', border: '1px solid rgba(0,0,0,0.06)' }}
                    />
                    <Box style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                      <Text size="sm" fw={600} ta="left" lineClamp={2} style={{ color: colorScheme === 'dark' ? '#f1f5f9' : '#1e293b', lineHeight: 1.3 }}>
                        {video.titulo}
                      </Text>
                      <Group gap={5} mt={2} wrap="wrap" justify="flex-start">
                        <Text size="11px" fw={600} ta="left" style={{ color: espColor }}>
                          {capitalizar(video.especialidadNombre)}
                        </Text>
                        {video.temaNombre && (
                          <Text size="11px" ta="left" c="dimmed">· {capitalizar(video.temaNombre)}</Text>
                        )}
                      </Group>
                    </Box>
                  </Group>
                </Box>
              );
            })}
          </SimpleGrid>
        )}

        {/* Sentinel: dispara la carga de más videos al acercarse */}
        {hayMas && (
          <Center ref={sentinelRef} py="md">
            <Loader size="sm" color="gray" />
          </Center>
        )}
      </Stack>

      {/* Modal para ver video */}
      <Modal
        opened={opened}
        onClose={close}
        title={
          <Group gap="xs" wrap="nowrap" style={{ maxWidth: '90%' }}>
            <Text fw={600} lineClamp={1}>
              {selectedVideo?.titulo || 'Video'}
            </Text>
            {selectedVideo?.id && (
              <QuejaSugerenciaButton
                tipoObjetivo="VIDEO"
                videoId={selectedVideo.id}
              />
            )}
          </Group>
        }
        size="xl"
        centered
      >
        {selectedVideo && (
          <Stack gap="md">
            <AspectRatio ratio={16 / 9}>
              <iframe
                src={selectedVideo.embedUrl || `https://www.youtube.com/embed/${selectedVideo.youtubeVideoId}`}
                title={selectedVideo.titulo}
                style={{ border: 0, borderRadius: '12px' }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </AspectRatio>
            {selectedVideo.descripcion && (
              <Text size="sm" c="dimmed">
                {selectedVideo.descripcion}
              </Text>
            )}
            <Group gap="sm">
              <Text size="sm" fw={600} style={{ color: colorEspecialidad(selectedVideo.especialidadId) }}>
                {capitalizar(selectedVideo.especialidadNombre)}
              </Text>
              {selectedVideo.temaNombre && (
                <Text size="sm" c="dimmed">· {capitalizar(selectedVideo.temaNombre)}</Text>
              )}
            </Group>
          </Stack>
        )}
      </Modal>
    </PageContainer>
  );
};

export default VideosEstudioPage;
